import classes from "./ContextSidebar.module.css";
import {ActionIcon, Group, Textarea, Title, Tooltip} from "@mantine/core";
import {useState, useCallback, useEffect, useRef} from "react";
import {chatApi} from "../../chatApi";
import IconEdit from "../../../../assets/edit.svg?react";
import IconCheck from "../../../../assets/check.svg?react";
import IconX from "../../../../assets/x.svg?react";
import {t} from "i18next";

type EditableTitleProps = {
    title: string;
    threadId: string | null;
};

export const EditableTitle = ({title, threadId}: EditableTitleProps) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState(title);
    const [renameThread] = chatApi.endpoints.renameThread.useMutation();
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    // Reset edit state when title changes externally (e.g. thread switch)
    useEffect(() => {
        setIsEditing(false);
        setEditValue(title);
    }, [title]);

    const startEditing = useCallback(() => {
        setEditValue(title);
        setIsEditing(true);
    }, [title]);

    const cancelEditing = useCallback(() => {
        setIsEditing(false);
        setEditValue(title);
    }, [title]);

    const confirmRename = useCallback(() => {
        const trimmed = editValue.trim();
        if (!trimmed || !threadId) {
            cancelEditing();
            return;
        }
        if (trimmed === title) {
            setIsEditing(false);
            return;
        }
        renameThread({threadId, name: trimmed});
        setIsEditing(false);
    }, [editValue, threadId, title, renameThread, cancelEditing]);

    if (isEditing) {
        return (
            <>
                <Textarea
                    ref={textareaRef}
                    className={classes.titleInput}
                    value={editValue}
                    onChange={(e) => setEditValue(e.currentTarget.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            confirmRename();
                        }
                        if (e.key === "Escape") cancelEditing();
                    }}
                    onFocus={(e) => {
                        const len = e.currentTarget.value.length;
                        e.currentTarget.setSelectionRange(len, len);
                    }}
                    onBlur={confirmRename}
                    autoFocus
                    autosize
                    minRows={1}
                />
                <Group gap="xs">
                    <ActionIcon
                        variant="subtle"
                        color="green"
                        size="sm"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={confirmRename}
                        aria-label={t("common.rename")}
                    >
                        <IconCheck width={16} />
                    </ActionIcon>
                    <ActionIcon
                        variant="subtle"
                        color="gray"
                        size="sm"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={cancelEditing}
                        aria-label={t("common.cancel")}
                    >
                        <IconX width={16} />
                    </ActionIcon>
                </Group>
            </>
        );
    }

    return (
        <>
            <Title order={4} size="h3" fw={400}>
                {title}
            </Title>
            {threadId && (
                <Tooltip label={t("common.rename")}>
                    <ActionIcon
                        className={classes.editButton}
                        variant="subtle"
                        color="gray"
                        size="sm"
                        onClick={startEditing}
                        aria-label={t("common.rename")}
                    >
                        <IconEdit width={16} />
                    </ActionIcon>
                </Tooltip>
            )}
        </>
    );
};
